// ---------------------------------------------------------------------------
// XP-5 (owner, 26 October 2026). Verbunk signs its user in on its own host.
//
// One Clerk instance serves the suite and its application name is "event.clinic". Clerk's
// embedded sign-in is mounted on Verbunk's own sign-in page instead of sending anyone to the
// instance's Account Portal (accounts.event.clinic): every string Clerk would template with
// `{{applicationName}}` is handed over already reading "Verbunk", the mark is Verbunk's, and
// Clerk's own sign-in and sign-up links point back at this page.
// ---------------------------------------------------------------------------

export const PRODUCT_NAME = "Verbunk";

/** Every `{{applicationName}}` in a Clerk localisation, replaced by `name`. */
export function named<T>(value: T, name: string): T {
  if (typeof value === "string") return value.split("{{applicationName}}").join(name) as T;
  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, named(v, name)])) as T;
  }
  return value;
}

/** `over` laid on `base`, key by key, so a key the translation lacks keeps the base's string. */
export function layered(base: unknown, over: unknown): unknown {
  if (!over || typeof over !== "object" || !base || typeof base !== "object") return over ?? base;
  const out: Record<string, unknown> = { ...(base as Record<string, unknown>) };
  for (const [k, v] of Object.entries(over)) out[k] = layered(out[k], v);
  return out;
}

/**
 * What `Clerk.load` is given on this host. `origin` is `window.location.origin`. English lies
 * under the translation: hu-HU lacks keys (signIn.start.titleCombined, measured on Velawel's
 * production), which Clerk would otherwise fill with its default, templated with "event.clinic".
 */
/** The action colour as the stylesheet declares it (design/semantic.css), read where there is a page; no second copy of the value. */
function brandColour(): string | undefined {
  const g = globalThis as unknown as { document?: { documentElement: object }; getComputedStyle?: (e: object) => { getPropertyValue(n: string): string } };
  if (!g.document || !g.getComputedStyle) return undefined;
  return g.getComputedStyle(g.document.documentElement).getPropertyValue("--action").trim() || undefined;
}

export function ownSignInOptions(english: object, translation: object | null, origin: string) {
  const localization = layered(english, translation) as Record<string, unknown>;
  return {
    signInUrl: `${origin}/sign-in`,
    signUpUrl: `${origin}/sign-in`,
    localization: named(localization, PRODUCT_NAME),
    appearance: {
      layout: { logoImageUrl: `${origin}/icon.svg`, logoLinkUrl: origin },
      variables: { colorPrimary: brandColour(), fontFamily: "inherit" },
    },
  };
}
